import { useMemo, useState, useCallback, useEffect } from 'react';
import { useTransactions } from './useTransactions';
import { useGoals } from './useGoals';
import { useDebts } from './useDebts';
import { useCategoryBudgets } from './useCategoryBudgets';
import { useMonthlySummary } from './useMonthlySummary';
import { useAIConsent } from './useAIConsent';
import { supabase } from '@/integrations/supabase/client';

interface DailyTip {
  id: string;
  emoji: string;
  title: string;
  message: string;
  priority: 'high' | 'medium' | 'low';
  category: 'spending' | 'goals' | 'debts' | 'general' | 'ai';
  source?: 'rule' | 'ai';
}

interface FinancialContext {
  totalIncome: number;
  totalExpenses: number;
  savingsRate: number;
  debtRatio: number;
  focoScore: number;
  topExpenseCategories: { category: string; amount: number; percentage: number }[];
  goalsProgress: { name: string; progress: number; daysRemaining: number | null }[];
  debtsInfo: { name: string; balance: number; interestRate: number; progress: number }[];
  recentTrends: {
    incomeChange: number;
    expenseChange: number;
    savingsChange: number;
  };
}

const categoryLabels: Record<string, string> = {
  food: 'Alimentación',
  transport: 'Transporte',
  entertainment: 'Entretenimiento',
  health: 'Salud',
  education: 'Educación',
  shopping: 'Compras',
  utilities: 'Servicios',
  personal_care: 'Cuidado personal',
  housing: 'Vivienda',
  subscriptions: 'Suscripciones',
};

export function useDailyTips() {
  const currentDate = new Date();
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth() + 1;
  const previousMonth = month === 1 ? 12 : month - 1;
  const previousYear = month === 1 ? year - 1 : year;

  const { transactions: currentTransactions } = useTransactions(year, month);
  const { transactions: previousTransactions } = useTransactions(previousYear, previousMonth);
  const { goals } = useGoals();
  const { debts } = useDebts();
  const { budgets } = useCategoryBudgets();
  const { summary: currentSummary } = useMonthlySummary(year, month);
  const { summary: previousSummary } = useMonthlySummary(previousYear, previousMonth);
  const { hasConsent: hasAIConsent } = useAIConsent();

  const [aiTip, setAiTip] = useState<DailyTip | null>(null);
  const [isLoadingAi, setIsLoadingAi] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  // Build financial context for AI
  const financialContext = useMemo((): FinancialContext | null => {
    if (!currentSummary) return null;

    const totalIncome = currentSummary.total_income;
    const totalExpenses = currentSummary.total_fixed_expenses + currentSummary.total_variable_expenses;
    
    // Calculate top expense categories
    const spendingByCategory: Record<string, number> = {};
    currentTransactions
      .filter(t => t.type === 'variable_expense' || t.type === 'fixed_expense')
      .forEach(t => {
        spendingByCategory[t.category] = (spendingByCategory[t.category] || 0) + Number(t.amount);
      });

    const topExpenseCategories = Object.entries(spendingByCategory)
      .map(([category, amount]) => ({
        category: categoryLabels[category] || category,
        amount,
        percentage: totalExpenses > 0 ? (amount / totalExpenses) * 100 : 0,
      }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 5);

    // Goals progress
    const activeGoals = goals.filter(g => g.status === 'active');
    const goalsProgress = activeGoals.map(goal => {
      const progress = (Number(goal.current_amount) / Number(goal.target_amount)) * 100;
      let daysRemaining: number | null = null;
      if (goal.deadline) {
        const deadline = new Date(goal.deadline);
        daysRemaining = Math.ceil((deadline.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24));
      }
      return { name: goal.name, progress, daysRemaining };
    });

    // Debts info
    const activeDebts = debts.filter(d => d.status === 'active');
    const debtsInfo = activeDebts.map(debt => ({
      name: debt.name,
      balance: Number(debt.current_balance),
      interestRate: Number(debt.interest_rate),
      progress: ((Number(debt.initial_balance) - Number(debt.current_balance)) / Number(debt.initial_balance)) * 100,
    }));

    // Recent trends
    const prevIncome = previousSummary?.total_income || 0;
    const prevExpenses = (previousSummary?.total_fixed_expenses || 0) + (previousSummary?.total_variable_expenses || 0);
    const prevSavings = previousSummary?.total_savings || 0;

    const recentTrends = {
      incomeChange: prevIncome > 0 ? ((totalIncome - prevIncome) / prevIncome) * 100 : 0,
      expenseChange: prevExpenses > 0 ? ((totalExpenses - prevExpenses) / prevExpenses) * 100 : 0,
      savingsChange: prevSavings > 0 ? ((currentSummary.total_savings - prevSavings) / prevSavings) * 100 : 0,
    };

    return {
      totalIncome,
      totalExpenses,
      savingsRate: currentSummary.savings_rate,
      debtRatio: currentSummary.debt_ratio,
      focoScore: currentSummary.foco_score,
      topExpenseCategories,
      goalsProgress,
      debtsInfo,
      recentTrends,
    };
  }, [currentSummary, previousSummary, currentTransactions, goals, debts, currentDate]);

  // Fetch AI tip
  const fetchAiTip = useCallback(async () => {
    if (!financialContext || isLoadingAi) return;

    // Check if we already have a tip for today in localStorage
    const today = currentDate.toISOString().split('T')[0];
    const cachedTip = localStorage.getItem('aiDailyTip');
    const cachedDate = localStorage.getItem('aiDailyTipDate');

    if (cachedTip && cachedDate === today) {
      try {
        setAiTip(JSON.parse(cachedTip));
        return;
      } catch {
        // Invalid cache, continue to fetch
      }
    }

    setIsLoadingAi(true);
    setAiError(null);

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        setAiError('Inicia sesión para ver tips con IA');
        return;
      }

      const { data, error } = await supabase.functions.invoke(
        'generate-daily-tip',
        {
          body: { financialContext },
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        },
      );

      if (error) throw error;

      if (data?.tip) {
        const newAiTip: DailyTip = {
          id: 'ai-tip',
          emoji: '🤖',
          title: 'Tip personalizado con IA',
          message: data.tip,
          priority: 'high',
          category: 'ai',
          source: 'ai',
        };
        setAiTip(newAiTip);
        
        // Cache the tip
        localStorage.setItem('aiDailyTip', JSON.stringify(newAiTip));
        localStorage.setItem('aiDailyTipDate', today);
      } else if (data?.fallback) {
        // AI failed, will use rule-based tip
        setAiError('AI no disponible, usando tip basado en reglas');
      }
    } catch (error) {
      console.error('Error fetching AI tip:', error);
      setAiError('Error al generar tip con IA');
    } finally {
      setIsLoadingAi(false);
    }
  }, [financialContext, isLoadingAi, currentDate]);

  // Auto-fetch AI tip when context is ready AND user has AI consent
  useEffect(() => {
    if (hasAIConsent && financialContext && financialContext.totalIncome > 0) {
      fetchAiTip();
    }
  }, [financialContext?.totalIncome, hasAIConsent]);

  // Rule-based tips (fallback and supplementary)
  const ruleTips = useMemo(() => {
    const generatedTips: DailyTip[] = [];
    const dayOfMonth = currentDate.getDate();
    
    const currentSpendingByCategory: Record<string, number> = {};
    const previousSpendingByCategory: Record<string, number> = {};
    
    currentTransactions
      .filter(t => t.type === 'variable_expense')
      .forEach(t => {
        currentSpendingByCategory[t.category] = (currentSpendingByCategory[t.category] || 0) + Number(t.amount);
      });
    
    previousTransactions
      .filter(t => t.type === 'variable_expense')
      .forEach(t => {
        previousSpendingByCategory[t.category] = (previousSpendingByCategory[t.category] || 0) + Number(t.amount);
      });

    // Tip: Gasto alto en una categoría
    Object.entries(currentSpendingByCategory).forEach(([category, amount]) => {
      const previousAmount = previousSpendingByCategory[category] || 0;
      if (previousAmount > 0 && amount > previousAmount * 1.3) {
        const increase = Math.round(((amount - previousAmount) / previousAmount) * 100);
        const categoryName = categoryLabels[category] || category;
        generatedTips.push({
          id: `spending-increase-${category}`,
          emoji: '📊',
          title: `Gasto creciente en ${categoryName}`,
          message: `Tu gasto en ${categoryName.toLowerCase()} aumentó ${increase}% vs el mes pasado. Considera revisar si es necesario.`,
          priority: increase > 50 ? 'high' : 'medium',
          category: 'spending',
          source: 'rule',
        });
      }
    });

    // Tip: Presupuesto cerca de límite
    budgets
      .filter(b => b.is_active)
      .forEach(budget => {
        const spent = currentSpendingByCategory[budget.category] || 0;
        const percentage = (spent / Number(budget.monthly_limit)) * 100;
        const categoryName = categoryLabels[budget.category] || budget.category;
        
        if (percentage >= 70 && percentage < 90) {
          generatedTips.push({
            id: `budget-warning-${budget.category}`,
            emoji: '⚠️',
            title: `Presupuesto al ${Math.round(percentage)}%`,
            message: `Ya usaste ${Math.round(percentage)}% de tu presupuesto de ${categoryName.toLowerCase()}. Te quedan ${Math.round(Number(budget.monthly_limit) - spent)} para el resto del mes.`,
            priority: 'medium',
            category: 'spending',
            source: 'rule',
          });
        }
      });

    // Tip: Meta financiera con buen progreso
    const activeGoals = goals.filter(g => g.status === 'active');
    activeGoals.forEach(goal => {
      const progress = (Number(goal.current_amount) / Number(goal.target_amount)) * 100;
      
      if (progress >= 75 && progress < 100) {
        generatedTips.push({
          id: `goal-almost-${goal.id}`,
          emoji: '🎯',
          title: '¡Tu meta está cerca!',
          message: `Estás al ${Math.round(progress)}% de completar "${goal.name}". ¡Un poco más y lo logras!`,
          priority: 'low',
          category: 'goals',
          source: 'rule',
        });
      } else if (progress < 25 && goal.deadline) {
        const deadline = new Date(goal.deadline);
        const daysRemaining = Math.ceil((deadline.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24));
        if (daysRemaining < 60 && daysRemaining > 0) {
          generatedTips.push({
            id: `goal-behind-${goal.id}`,
            emoji: '📅',
            title: 'Acelera tu meta',
            message: `Tu meta "${goal.name}" tiene ${daysRemaining} días restantes pero solo ${Math.round(progress)}% de progreso. Considera aumentar tus aportes.`,
            priority: 'high',
            category: 'goals',
            source: 'rule',
          });
        }
      }
    });

    // Tip: Meta sin progreso reciente
    if (activeGoals.length > 0) {
      const goalWithoutProgress = activeGoals.find(g => Number(g.current_amount) === 0);
      if (goalWithoutProgress) {
        generatedTips.push({
          id: `goal-start-${goalWithoutProgress.id}`,
          emoji: '🚀',
          title: 'Empieza tu meta',
          message: `Tu meta "${goalWithoutProgress.name}" aún no tiene aportes. ¡El primer paso es el más importante!`,
          priority: 'medium',
          category: 'goals',
          source: 'rule',
        });
      }
    }

    // Tip: Deudas con próximo vencimiento
    const activeDebts = debts.filter(d => d.status === 'active');
    activeDebts.forEach(debt => {
      if (debt.due_day) {
        const daysUntilDue = debt.due_day - dayOfMonth;
        if (daysUntilDue > 0 && daysUntilDue <= 5) {
          generatedTips.push({
            id: `debt-due-${debt.id}`,
            emoji: '💳',
            title: 'Pago próximo',
            message: `Tu deuda "${debt.name}" vence en ${daysUntilDue} días. El pago mínimo es de ${Number(debt.minimum_payment).toLocaleString()}.`,
            priority: 'high',
            category: 'debts',
            source: 'rule',
          });
        }
      }
    });

    // Tip: Deuda casi pagada
    activeDebts.forEach(debt => {
      const progress = ((Number(debt.initial_balance) - Number(debt.current_balance)) / Number(debt.initial_balance)) * 100;
      if (progress >= 80 && progress < 100) {
        generatedTips.push({
          id: `debt-almost-${debt.id}`,
          emoji: '🎉',
          title: '¡Casi libre de deuda!',
          message: `Solo te falta ${Number(debt.current_balance).toLocaleString()} para liquidar "${debt.name}". ¡Vas excelente!`,
          priority: 'low',
          category: 'debts',
          source: 'rule',
        });
      }
    });

    // Tips generales
    if (dayOfMonth <= 5) {
      generatedTips.push({
        id: 'general-start-month',
        emoji: '📝',
        title: 'Inicio de mes',
        message: 'Es buen momento para revisar tu presupuesto mensual y establecer límites de gasto por categoría.',
        priority: 'low',
        category: 'general',
        source: 'rule',
      });
    } else if (dayOfMonth >= 25) {
      generatedTips.push({
        id: 'general-end-month',
        emoji: '📈',
        title: 'Cierre de mes',
        message: 'Revisa cómo te fue este mes. ¿Alcanzaste tus metas de ahorro? Ajusta tu plan para el próximo mes.',
        priority: 'low',
        category: 'general',
        source: 'rule',
      });
    }

    if (activeGoals.length === 0) {
      generatedTips.push({
        id: 'general-no-goals',
        emoji: '🎯',
        title: 'Define tus metas',
        message: 'No tienes metas financieras activas. Establece al menos una meta para motivar tu ahorro.',
        priority: 'medium',
        category: 'general',
        source: 'rule',
      });
    }

    const priorityOrder = { high: 0, medium: 1, low: 2 };
    generatedTips.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);

    return generatedTips;
  }, [currentTransactions, previousTransactions, goals, debts, budgets, currentDate]);

  // Combine AI and rule-based tips
  const allTips = useMemo(() => {
    if (aiTip) {
      return [aiTip, ...ruleTips];
    }
    return ruleTips;
  }, [aiTip, ruleTips]);

  // Get daily tip - prefer AI tip if available
  const dailyTip = useMemo(() => {
    if (aiTip) {
      return aiTip;
    }

    if (ruleTips.length === 0) {
      return {
        id: 'default',
        emoji: '💡',
        title: 'Tip del día',
        message: 'Registra tus gastos diariamente para tener un mejor control de tus finanzas.',
        priority: 'low' as const,
        category: 'general' as const,
        source: 'rule' as const,
      };
    }
    
    const dayOfYear = Math.floor((currentDate.getTime() - new Date(currentDate.getFullYear(), 0, 0).getTime()) / (1000 * 60 * 60 * 24));
    
    const highPriorityTips = ruleTips.filter(t => t.priority === 'high');
    if (highPriorityTips.length > 0) {
      return highPriorityTips[dayOfYear % highPriorityTips.length];
    }
    
    return ruleTips[dayOfYear % ruleTips.length];
  }, [aiTip, ruleTips, currentDate]);

  return {
    tips: allTips,
    dailyTip,
    hasHighPriorityTips: allTips.some(t => t.priority === 'high'),
    isLoadingAi,
    aiError,
    refreshAiTip: fetchAiTip,
  };
}
